import { Button } from '@/components/ui/primitives';
import {
    MarketingPage,
    Hero,
    ContextNote,
    FeatureGrid,
    CtaBand,
    PageFooter,
    Dateline,
} from '@/components/marketing';
import { pageForRoute } from '@/site/pageRegistry';

const PAGE = pageForRoute('/bluebeam-revu-free-alternative');

const CHIPS = ['No subscription', 'No install', 'No account', 'Files stay local'] as const;

const FEATURE_CARDS = [
    {
        title: 'Full markup toolset',
        body: 'Line, polyline, polygon, rectangle, text, cloud, callout, arrow, ellipse, highlight, and ink tools are all available in the free browser workflow.',
    },
    {
        title: 'Measurement to scale',
        body: 'Calibrate the drawing scale once, then measure length, area, and perimeter directly on the PDF for quick checks and takeoffs.',
    },
    {
        title: 'Private and local',
        body: 'Files are processed in your browser on your device and are not uploaded. You can keep markups local from open through export.',
        accent: true,
    },
    {
        title: 'Export what you reviewed',
        body: 'Export an annotated PDF for the field or design team, then export the markup list to CSV for downstream tracking.',
    },
    {
        title: 'Made for drawing review',
        body: 'Use it for submittals, RFIs, punch review, and design coordination when the job is to get comments onto sheets quickly and clearly.',
    },
    {
        title: 'Starts instantly',
        body: 'There is nothing to deploy to a workstation. Open the URL, choose a PDF, and start reviewing in the browser right away.',
    },
] as const;

export function BluebeamRevuFreeAlternativePage() {
    return (
        <MarketingPage head={{ ...PAGE.head, canonical: PAGE.canonical }}>
            <main>
                <Hero
                    eyebrow="Free browser PDF markup for AEC"
                    title={<>Free PDF markup for drawing review.<br />Opens in your browser.</>}
                    lead="If you are evaluating Bluebeam Revu and want a free way to review drawings locally, PDF Markup Tool gives you markup, measurement, and export in any browser with no subscription, no install, and no account."
                    cta={<Button href="/" variant="primary" size="lg">Open a PDF</Button>}
                    chips={CHIPS}
                />
                <ContextNote text="PDF Markup Tool is built for architects, engineers, and contractors who need to redline drawings, review coordination comments, and send out an annotated PDF without handing project files to a remote service." />
                <FeatureGrid
                    sectionTitle="Everything needed for free drawing review"
                    sectionLead="Open a sheet in Chrome, Safari, Edge, or Firefox and work with the same local-first workflow from the first markup through export."
                    cards={FEATURE_CARDS}
                />
                <CtaBand
                    title="Open a drawing and start marking it up"
                    lead="Launch the tool, keep the file on your machine, and send back an annotated PDF when the review is done."
                    actions={
                        <>
                            <Button href="/" variant="primary" size="lg">Open a PDF</Button>
                            <Button href="/redline-pdf-drawing/" variant="ghost">
                                How to redline a PDF drawing
                            </Button>
                        </>
                    }
                />
                <section className="mktg-context">
                    <div className="mktg-container">
                        <p className="mktg-context__note">
                            Looking at the Revu 20 transition? See the <a href="/bluebeam-revu-20-end-of-life/">Bluebeam Revu 20 end-of-life timeline</a>.
                        </p>
                    </div>
                </section>
                <Dateline reviewed={PAGE.reviewed} />
            </main>
            <PageFooter tagline="Free browser-native PDF markup for drawing review" />
        </MarketingPage>
    );
}
